import type { AspectId, ChannelId, WorldState } from './types.js';
import { generateSeed, isValidSeed } from './utils/seed.js';

type Listener<T> = (value: T, previous: T) => void;

export interface Store<T> {
  get(): T;
  set(patch: Partial<T>): void;
  subscribe(listener: Listener<T>): () => void;
}

function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener<T>>();

  return {
    get(): T {
      return state;
    },
    set(patch: Partial<T>): void {
      const previous = state;
      const next = { ...state, ...patch } as T;
      let changed = false;
      for (const key in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
          if ((previous as Record<string, unknown>)[key] !== (next as Record<string, unknown>)[key]) {
            changed = true;
            break;
          }
        }
      }
      if (!changed) return;
      state = next;
      for (const listener of listeners) {
        listener(state, previous);
      }
    },
    subscribe(listener: Listener<T>): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type AppState = WorldState;

const defaultChannel: ChannelId = 'ch1';
const defaultAspect: AspectId = '16x9';

/** Pull a `?seed=xxxxxx` override from the URL, when valid. Browser-only. */
function seedFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('seed');
  if (raw && isValidSeed(raw)) return raw.toLowerCase();
  return null;
}

export function createInitialState(): AppState {
  return {
    blobs: [],
    aspect: defaultAspect,
    channel: defaultChannel,
    seed: seedFromUrl() ?? generateSeed(),
    bgMedia: null,
    bgMediaEnabled: false,
    bgFitMode: 'cover',
    bgOpacity: 1,
    objectSize: 0.28,
    // Phase 2 demoted these from interactive sliders to art-directed
    // constants. The state fields and shader uniforms are kept so the
    // controls can be re-promoted if needed.
    lineWidth: 16,
    marginWidth: 21,
    attraction: 0.04,
    // Phase 3 — placeholder hue values; the App constructor overwrites
    // these with the seed-derived complementary pair for the default
    // channel before any UI subscribers are attached.
    hueA: 0,
    hueB: 180,
    // Phase 4 — halftone defaults. ON by default; this changes the
    // visual baseline for every existing seed (turn off in the UI to
    // restore the pre-halftone look).
    halftoneEnabled: true,
    halftoneMode: 'oneColor',
    halftoneShape: 'circle',
    halftoneCellSize: 18,
    halftoneGap: 0.65,
    // Placeholder hues — App constructor overwrites from seed-derived
    // values before any UI subscriber is attached.
    halftoneHueOne: 0,
    halftoneHueTwoA: 0,
    halftoneHueTwoB: 180,
    titleWeight: 100,
    titleSize: 70,
    titleText: 'カナワツギ',
    dateText: '2026.05.21 Thu. – 05.27 Wed.',
    timeText: 'OPEN 12:00–20:00 [Wed. until 16:00]',
    titleOrientation: 'horizontal',
    isFullscreenMode: false,
    letterboxFrame: false,
  };
}

export const store: Store<AppState> = createStore<AppState>(createInitialState());
